import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, ArrowRight, Play, Check, X } from 'lucide-react';
import { Video as VideoIcon, Microscope, PenTool, Lightbulb, BookOpen, Loader2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { chapterAnnotationsAPI } from '@/api/chapterAnnotations';
import WatchContentRenderer from '@/components/watch/WatchContentRenderer';
import SimulationRenderer from '@/components/simulations/SimulationRenderer';
import BudleeAIIcon from './BudleeAIIcon';
import assetCacheService from '@/utils/assetCacheService';

const isYouTubeUrl = (url) => {
  if (!url) return false;
  const raw = String(url);
  return raw.includes('youtube.com') || raw.includes('youtu.be');
};

const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  const raw = String(url).trim();
  const patterns = [
    /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
  ];
  for (const pattern of patterns) {
    const match = raw.match(pattern);
    if (match && match[1]) {
      return `https://www.youtube.com/embed/${match[1]}?autoplay=0&mute=0&playsinline=1&rel=0&modestbranding=1`;
    }
  }
  if (raw.length === 11 && /^[a-zA-Z0-9_-]+$/.test(raw)) {
    return `https://www.youtube.com/embed/${raw}?autoplay=0&mute=0&playsinline=1&rel=0&modestbranding=1`;
  }
  return null;
};

const iconMap = {
  Watch: BookOpen,
  Video: VideoIcon,
  Explore: Microscope,
  Practice: PenTool,
  Curiosity: Lightbulb
};

const typeColors = {
  Watch: 'from-blue-500 to-purple-600',
  Video: 'from-red-500 via-pink-500 to-purple-500',
  Explore: 'from-green-500 to-teal-600',
  Practice: 'from-orange-500 to-red-600',
  Curiosity: 'from-yellow-500 to-amber-600'
};

export default function CardJourneyView({
  items,
  onExit,
  onComplete,
  isItemCompleted,
  currentIndex: controlledIndex,
  onIndexChange,
  onToggleChatMode,
  chatMode = false,
}) {
  const [uncontrolledIndex, setUncontrolledIndex] = useState(0);
  const currentIndex = typeof controlledIndex === 'number' ? controlledIndex : uncontrolledIndex;

  const setCurrentIndex = (nextIndex) => {
    const safeLength = Array.isArray(items) ? items.length : 0;
    const maxIndex = Math.max(0, safeLength - 1);
    const clampedIndex = Math.min(Math.max(0, Number(nextIndex) || 0), maxIndex);

    if (typeof controlledIndex !== 'number') {
      setUncontrolledIndex(clampedIndex);
    }
    if (typeof onIndexChange === 'function') {
      onIndexChange(clampedIndex);
    }
  };
  const [activityState, setActivityState] = useState('idle');
  const [timer, setTimer] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [clickedElements, setClickedElements] = useState(new Set());

  const [curiosityStep, setCuriosityStep] = useState(0);
  const [curiosityReveal, setCuriosityReveal] = useState(false);

  const [extrasByItemId, setExtrasByItemId] = useState({});
  const [videoProgressPercent, setVideoProgressPercent] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const videoElementRef = useRef(null);

  const parseDurationSeconds = (duration) => {
    if (typeof duration === 'number' && Number.isFinite(duration)) return duration;
    const raw = String(duration || '').trim();
    if (!raw) return 0;

    // e.g. "2 min", "10 mins"
    const minMatch = raw.match(/(\d+)\s*(min|mins|minute|minutes)/i);
    if (minMatch) return Math.max(0, parseInt(minMatch[1], 10) || 0) * 60;

    // e.g. "3:45" or "01:10"
    const clockMatch = raw.match(/^(\d+):(\d{2})$/);
    if (clockMatch) {
      const m = parseInt(clockMatch[1], 10) || 0;
      const s = parseInt(clockMatch[2], 10) || 0;
      return Math.max(0, m * 60 + s);
    }

    // e.g. "120s"
    const secMatch = raw.match(/(\d+)\s*(s|sec|secs|second|seconds)/i);
    if (secMatch) return Math.max(0, parseInt(secMatch[1], 10) || 0);

    return 0;
  };

  const parseCuriositySegments = (rawText) => {
    const raw = String(rawText || '').trim();
    if (!raw) return { intro: '', explanation: '' };

    const lines = raw.split('\n');
    const introStartIdx = lines.findIndex((l) => /^\s*(quick\s*idea|idea)\s*:?\s*$/i.test(String(l || '').trim()));
    const explanationStartIdx = lines.findIndex((l) => /^\s*(explanation|answer)\s*:?\s*$/i.test(String(l || '').trim()));

    if (introStartIdx !== -1 || explanationStartIdx !== -1) {
      const introStart = introStartIdx !== -1 ? introStartIdx + 1 : -1;
      const explanationStart = explanationStartIdx !== -1 ? explanationStartIdx + 1 : -1;
      const introEnd =
        introStart !== -1
          ? (explanationStartIdx !== -1 && explanationStartIdx > introStartIdx ? explanationStartIdx : lines.length)
          : -1;
      const intro = introStart !== -1 ? lines.slice(introStart, introEnd).join('\n').trim() : '';
      const explanation = explanationStart !== -1 ? lines.slice(explanationStart).join('\n').trim() : '';
      return { intro, explanation };
    }

    const paragraphs = raw.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
    if (paragraphs.length === 1) return { intro: '', explanation: paragraphs[0] };
    return { intro: paragraphs[0], explanation: paragraphs.slice(1).join('\n\n') };
  };

  const baseCurrentItem = items[currentIndex];
  const currentItemExtras = baseCurrentItem?.id ? extrasByItemId[String(baseCurrentItem.id)] : null;
  const currentItem = baseCurrentItem
    ? {
      ...baseCurrentItem,
      ...currentItemExtras,
      durationSeconds:
        baseCurrentItem.durationSeconds ??
        currentItemExtras?.durationSeconds ??
        parseDurationSeconds(baseCurrentItem.duration),
    }
    : null;

  const effectiveVideoUrl =
    currentItem?.type === 'Video'
      ? (currentItem?.youtube_url || currentItem?.video_file_url || currentItemExtras?.videoUrl || null)
      : null;
  const viewType = currentItem?.type;
  const viewTypeLabel =
    viewType === 'Watch'
      ? 'Learn and Discover'
      : viewType === 'Video'
        ? 'Watch Video'
        : viewType;
  const isCuriosity = viewType === 'Curiosity';
  const Icon = iconMap[viewType];
  const colorGradient = typeColors[viewType];
  const isAlreadyCompleted = isItemCompleted(currentItem?.id);

  // Lazily load Practice questions / Curiosity content from backend for annotation-based items.
  useEffect(() => {
    if (!currentItem?.annotation_id || !currentItem?.id) return;
    const itemId = String(currentItem.id);
    const existing = extrasByItemId[itemId];

    // Learn & Discover (Watch) content
    if (currentItem?.type === 'Watch' && !existing?.watchContent && !existing?.watchLoading && !existing?.watchError) {
      setExtrasByItemId((prev) => ({
        ...prev,
        [itemId]: {
          ...(prev[itemId] || {}),
          watchLoading: true,
          watchError: null,
        },
      }));

      chapterAnnotationsAPI
        .getWatchContent(currentItem.annotation_id)
        .then((data) => {
          setExtrasByItemId((prev) => ({
            ...prev,
            [itemId]: {
              ...(prev[itemId] || {}),
              watchContent: data,
              watchLoading: false,
              watchError: null,
            },
          }));
        })
        .catch(() => {
          setExtrasByItemId((prev) => ({
            ...prev,
            [itemId]: {
              ...(prev[itemId] || {}),
              watchLoading: false,
              watchError: 'Failed to load Learn & Discover content',
            },
          }));
        });
    }

    // Practice questions
    if (currentItem?.type === 'Practice' && !existing?.questions) {
      chapterAnnotationsAPI
        .getPracticeQuestions(currentItem.annotation_id)
        .then((data) => {
          const transformedQuestions = (data?.questions || []).map((q, idx) => {
            const correctIndex = typeof q.correctIndex === 'number' ? q.correctIndex : parseInt(q.correctIndex, 10);
            return {
              id: q.id || `q${idx + 1}`,
              question: q.question,
              options: q.options || [],
              correctIndex: Number.isFinite(correctIndex) ? correctIndex : 0,
              explanation: q.explanation,
            };
          });

          setExtrasByItemId((prev) => ({
            ...prev,
            [itemId]: {
              ...(prev[itemId] || {}),
              questions: transformedQuestions,
            },
          }));
        })
        .catch(() => {
          // Keep UI usable even if backend is unavailable.
        });
    }

    // Curiosity content
    if (currentItem?.type === 'Curiosity' && !existing?.curiosityContent && !existing?.content) {
      chapterAnnotationsAPI
        .getCuriosityContent(currentItem.annotation_id)
        .then((data) => {
          const content = data?.content || data?.text || '';
          setExtrasByItemId((prev) => ({
            ...prev,
            [itemId]: {
              ...(prev[itemId] || {}),
              curiosityContent: data || null,
              content,
            },
          }));
        })
        .catch(() => {
          // Keep UI usable even if backend is unavailable.
        });
    }

    // Video URL (for Video items where list payload didn't include video_file_url yet)
    if (currentItem?.type === 'Video' && !currentItem?.video_file_url && !currentItem?.youtube_url && !existing?.videoUrl && !existing?.videoLoading) {
      setExtrasByItemId((prev) => ({
        ...prev,
        [itemId]: {
          ...(prev[itemId] || {}),
          videoLoading: true,
        },
      }));

      chapterAnnotationsAPI
        .getAnnotation(currentItem.annotation_id)
        .then((data) => {
          setExtrasByItemId((prev) => ({
            ...prev,
            [itemId]: {
              ...(prev[itemId] || {}),
              videoUrl: data?.video_file_url || null,
              videoLoading: false,
            },
          }));
        })
        .catch(() => {
          setExtrasByItemId((prev) => ({
            ...prev,
            [itemId]: {
              ...(prev[itemId] || {}),
              videoLoading: false,
            },
          }));
        });
    }
    // Explore Simulation (NEW)
    if (currentItem?.type === 'Explore' && !existing?.exploreSimulation && !existing?.simulationLoading) {
      setExtrasByItemId((prev) => ({
        ...prev,
        [itemId]: {
          ...(prev[itemId] || {}),
          simulationLoading: true,
          simulationError: null,
        },
      }));

      chapterAnnotationsAPI
        .getExploreSimulation(currentItem.annotation_id)
        .then((data) => {
          // Parse simulation_data if it's a string (with Python dict fix)
          if (data && typeof data.simulation_data === 'string') {
            try {
              data.simulation_data = JSON.parse(data.simulation_data);
            } catch (e) {
              console.warn('Standard JSON parse failed, trying python-dict fix:', e);
              try {
                // Last resort fix for Python dict strings
                let fixedJson = data.simulation_data
                  .replace(/'\s*:\s*'/g, '": "')
                  .replace(/'\s*:\s*\[/g, '": [')
                  .replace(/'\s*:\s*\{/g, '": {')
                  .replace(/'\s*,\s*'/g, '", "')
                  .replace(/\{\s*'/g, '{ "')
                  .replace(/\[\s*'/g, '[ "')
                  .replace(/'\s*\}/g, '" }')
                  .replace(/'\s*\]/g, '" ]')
                  .replace(/:\s*None/g, ': null')
                  .replace(/:\s*True/g, ': true')
                  .replace(/:\s*False/g, ': false');
                data.simulation_data = JSON.parse(fixedJson);
              } catch (e2) {
                console.error('Error parsing simulation_data:', e2);
                throw new Error('Invalid simulation data format');
              }
            }
          }

          setExtrasByItemId((prev) => ({
            ...prev,
            [itemId]: {
              ...(prev[itemId] || {}),
              exploreSimulation: data,
              simulationLoading: false,
              simulationError: null,
            },
          }));
        })
        .catch((err) => {
          console.error('Failed to load simulation:', err);
          setExtrasByItemId((prev) => ({
            ...prev,
            [itemId]: {
              ...(prev[itemId] || {}),
              simulationLoading: false,
              simulationError: 'Failed to load interactive simulation',
            },
          }));
        });
    }
  }, [currentItem?.annotation_id, currentItem?.id, currentItem?.type]);

  // Reset activity state when moving to new item
  useEffect(() => {
    setActivityState(isAlreadyCompleted ? 'completed' : 'idle');
    setTimer(0);
    setVideoProgressPercent(0);
    setSelectedAnswers({});
    setClickedElements(new Set());
    setCuriosityStep(0);
    setCuriosityReveal(false);
  }, [currentIndex, isAlreadyCompleted]);

  // Video timer
  useEffect(() => {
    if (viewType === 'Video' && activityState === 'in-progress') {
      const interval = setInterval(() => {
        setTimer(prev => {
          const newTime = prev + 1;
          if (newTime >= currentItem.durationSeconds) {
            setActivityState('completed');
            if (!isAlreadyCompleted) {
              onComplete(currentItem.id, currentItem.xpReward);
            }
            clearInterval(interval);
            return currentItem.durationSeconds;
          }
          return newTime;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [activityState, currentItem, isAlreadyCompleted, onComplete, viewType]);

  const handleStartVideo = () => {
    setActivityState('in-progress');
  };

  const handleVideoTimeUpdate = (e) => {
    try {
      const el = e?.currentTarget;
      if (!el || !el.duration || !Number.isFinite(el.duration) || el.duration <= 0) return;
      const pct = Math.min(100, Math.max(0, (el.currentTime / el.duration) * 100));
      setVideoProgressPercent(pct);
      if (activityState === 'idle') setActivityState('in-progress');
    } catch {
      // ignore
    }
  };

  const handleVideoEnded = () => {
    setActivityState('completed');
    if (!isAlreadyCompleted) {
      onComplete(currentItem.id, currentItem.xpReward);
    }
  };

  const handleSimulationClick = (elementId) => {
    const newClicked = new Set(clickedElements);
    newClicked.add(elementId);
    setClickedElements(newClicked);

    if (newClicked.size >= 3) {
      setActivityState('completed');
      if (!isAlreadyCompleted) {
        onComplete(currentItem.id, currentItem.xpReward);
      }
    }
  };

  // Ensure HTML5 video element respects playback rate and is unmuted
  useEffect(() => {
    const v = videoElementRef.current;
    if (v) {
      try {
        v.muted = false;
        v.volume = 1;
        v.playbackRate = playbackRate || 1;
      } catch (e) {
        // ignore
      }
    }
  }, [playbackRate]);

  const handleAnswerSelect = (questionId, optionIndex) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: optionIndex
    }));
  };

  const handleSubmitPractice = () => {
    const allAnswered = currentItem.questions?.every(q => selectedAnswers[q.id] !== undefined);
    if (allAnswered) {
      setActivityState('completed');
      if (!isAlreadyCompleted) {
        onComplete(currentItem.id, currentItem.xpReward);
      }
    }
  };

  const handleMarkAsRead = () => {
    setActivityState('completed');
    if (!isAlreadyCompleted) {
      onComplete(currentItem.id, currentItem.xpReward);
    }
  };

  const handleNext = () => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // All activities completed
      onExit();
    }
  };

  const handleNavPrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleNavNext = () => {
    if (currentIndex < items.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const canProceed = activityState === 'completed' || isAlreadyCompleted;
  const videoProgress = effectiveVideoUrl && !isYouTubeUrl(effectiveVideoUrl)
    ? videoProgressPercent
    : (currentItem?.durationSeconds > 0 ? (timer / currentItem.durationSeconds) * 100 : 0);
  const allQuestionsAnswered = currentItem?.questions?.every(q => selectedAnswers[q.id] !== undefined);

  // Get section label from annotation data (matching ActivityProgressList logic)
  const getSectionLabel = (item) => {
    const name = typeof item?.section_name === 'string' ? item.section_name.trim() : '';
    const number = typeof item?.section_number === 'string' ? item.section_number.trim() : '';
    if (name) return name;
    if (number) return `Section ${number}`;
    return null;
  };

  const sectionLabel = getSectionLabel(currentItem);

  // Activity title (not the section name)
  const activityTitle = (currentItem?.title || '').toString().trim() || undefined;
  const displayTitle =
    viewType === 'Watch' && (!activityTitle || activityTitle === 'Watch Video')
      ? 'Learn and Discover'
      : activityTitle;

  const oneLinerText = typeof currentItem?.one_liner === 'string' ? currentItem.one_liner.trim() : '';
  const headerTitle = isCuriosity ? (oneLinerText || 'Curiosity') : displayTitle;
  const headerDescription = isCuriosity ? null : (oneLinerText || currentItem?.description || null);
  const resolvedCuriosity = (() => {
    if (currentItem?.type !== 'Curiosity') return null;
    const curiosityPayload = currentItem?.curiosityContent || null;
    const title = curiosityPayload?.title || currentItem?.title || 'Curiosity';
    const payloadIntro = curiosityPayload?.intro;
    const payloadExplanation = curiosityPayload?.content;

    const rawFallback = currentItem?.content || '';
    const parsed = parseCuriositySegments(rawFallback);

    const intro = (typeof payloadIntro === 'string' ? payloadIntro : '') || parsed.intro || '';
    const explanation =
      (typeof payloadExplanation === 'string' ? payloadExplanation : '') || parsed.explanation || '';

    const readingTime = curiosityPayload?.reading_time;
    return { title, intro, explanation, readingTime };
  })();

  if (!currentItem) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center p-6 text-center text-gray-500">
        <div className="max-w-md">
          <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-medium mb-2">No activities found</h3>
          <p className="text-sm mb-6">There are no items in this learning pack to display.</p>
          <Button
            onClick={onExit}
            variant="outline"
            className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--sidebar-hover))] hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 sm:mb-6">
        <Button
          onClick={onExit}
          variant="outline"
          className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--sidebar-hover))] hover:text-white h-8 sm:h-10 text-xs sm:text-sm px-2 sm:px-4"
        >
          <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">Back</span>
          <span className="sm:hidden">Exit</span>
        </Button>

        {/* Progress Panel - Show on all screens */}
        <div className="text-center">
          <div className="text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2">Learning Pack Progress</div>
          <div className="flex items-center justify-center gap-2 sm:gap-3">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleNavPrev}
              disabled={currentIndex <= 0}
              className="h-7 w-7 sm:h-8 sm:w-8 bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--sidebar-hover))] hover:text-white disabled:opacity-40"
              aria-label="Previous activity"
            >
              <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
            </Button>

            <div className="flex items-center gap-1.5 sm:gap-2 hidden sm:flex">
              {items.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setCurrentIndex(idx)}
                  aria-label={`Go to activity ${idx + 1}`}
                  className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all ${idx < currentIndex
                    ? 'bg-green-500'
                    : idx === currentIndex
                      ? 'bg-[hsl(var(--primary))] scale-125'
                      : 'bg-gray-600'
                    }`}
                />
              ))}
            </div>

            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleNavNext}
              disabled={currentIndex >= items.length - 1}
              className="h-7 w-7 sm:h-8 sm:w-8 bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--sidebar-hover))] hover:text-white disabled:opacity-40"
              aria-label="Next activity"
            >
              <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
            </Button>
          </div>
          <div className="text-sm sm:text-base lg:text-lg font-bold text-foreground mt-1 sm:mt-2">
            {currentIndex + 1} of {items.length}
          </div>
        </div>

        <div className="w-20 sm:w-32" /> {/* Spacer for symmetry */}
      </div>

      {/* Section Header - Displayed prominently above the activity card */}
      {sectionLabel && (
        <div className="max-w-4xl mx-auto mb-2 sm:mb-4 px-2 sm:px-0">
          <div className="bg-[hsl(var(--card-bg))]/50 border border-[hsl(var(--card-border))]/50 rounded-lg px-3 sm:px-6 py-2 sm:py-3">
            <div className="flex items-center gap-2">
              <div className="w-1 h-4 sm:h-6 bg-[hsl(var(--primary))] rounded-full"></div>
              <h3 className="text-xs sm:text-sm lg:text-lg font-bold text-foreground uppercase tracking-wide">
                {sectionLabel}
              </h3>
            </div>
          </div>
        </div>
      )}

      {/* Activity Card */}
      <Card className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] max-w-4xl mx-auto">
        <div className="p-3 sm:p-6 lg:p-8 space-y-3 sm:space-y-4 lg:space-y-6">
          {/* Activity Header */}
          <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 mb-3 sm:mb-4 lg:mb-6">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-full bg-gradient-to-br ${colorGradient} flex items-center justify-center shadow-xl`}>
              <Icon className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-white" />
            </div>
            <div>
              <div className="text-xs sm:text-sm font-bold text-[hsl(var(--teal-vivid))] uppercase tracking-wide">
                {viewTypeLabel}
              </div>
              <h2 className="text-base sm:text-xl lg:text-2xl font-bold text-white">{headerTitle}</h2>
              <p className="text-gray-300 text-xs sm:text-sm">{currentItem.duration}</p>
            </div>
          </div>

          {headerDescription ? (
            <p className="text-gray-300 leading-relaxed text-xs sm:text-sm lg:text-base">{headerDescription}</p>
          ) : null}

          {/* Activity Content */}
          <div className="space-y-4">
            {/* WATCH (Learn & Discover) */}
            {viewType === 'Watch' && (
              <div className="space-y-4">
                {currentItemExtras?.watchLoading ? (
                  <div className="flex items-center justify-center py-6 sm:py-10">
                    <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-blue-400" />
                    <span className="ml-2 sm:ml-3 text-gray-300 text-xs sm:text-sm">Loading Learn & Discover...</span>
                  </div>
                ) : currentItemExtras?.watchError ? (
                  <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3 sm:p-4 text-center">
                    <p className="text-red-400 text-xs sm:text-sm">{currentItemExtras.watchError}</p>
                    <p className="text-gray-300 text-xs sm:text-sm mt-2">Please try again later.</p>
                  </div>
                ) : currentItemExtras?.watchContent?.content_data ? (
                  <WatchContentRenderer
                    watchContent={currentItemExtras.watchContent}
                    onComplete={() => {
                      setActivityState('completed');
                      if (!isAlreadyCompleted) {
                        onComplete(currentItem.id, currentItem.xpReward);
                      }
                    }}
                  />
                ) : (
                  <div className="bg-blue-900/20 border border-blue-500/50 rounded-lg p-4 sm:p-6 text-center">
                    <BookOpen className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 text-blue-400 opacity-75" />
                    <p className="text-blue-400 font-semibold text-sm sm:text-base lg:text-lg mb-2">Learn & Discover Content</p>
                    <p className="text-gray-300 text-xs sm:text-sm">
                      AI-generated interactive content will appear here when available.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* VIDEO */}
            {viewType === 'Video' && (
              <>
                <div className="relative bg-black/20 rounded-lg overflow-hidden border border-[hsl(var(--card-border))]">
                  {effectiveVideoUrl ? (
                    isYouTubeUrl(effectiveVideoUrl) ? (
                      <div className="relative w-full">
                        <div className="absolute top-3 right-3 z-20">
                          <select
                            value={playbackRate}
                            onChange={(e) => setPlaybackRate(Number(e.target.value))}
                            className="bg-[hsl(var(--card-bg))] text-sm !text-white border border-[hsl(var(--card-border))] rounded px-2 py-1"
                            title="Playback speed"
                          >
                            <option value={0.5}>0.5x</option>
                            <option value={0.75}>0.75x</option>
                            <option value={1}>1x</option>
                            <option value={1.25}>1.25x</option>
                            <option value={1.5}>1.5x</option>
                          </select>
                        </div>
                        <iframe
                          title={displayTitle || 'Video'}
                          src={getYouTubeEmbedUrl(effectiveVideoUrl)}
                          className="w-full aspect-video"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <div className="relative w-full">
                        <div className="absolute top-3 right-3 z-20">
                          <select
                            value={playbackRate}
                            onChange={(e) => setPlaybackRate(Number(e.target.value))}
                            className="bg-[hsl(var(--card-bg))] text-sm !text-white border border-[hsl(var(--card-border))] rounded px-2 py-1"
                            title="Playback speed"
                          >
                            <option value={0.5}>0.5x</option>
                            <option value={0.75}>0.75x</option>
                            <option value={1}>1x</option>
                            <option value={1.25}>1.25x</option>
                            <option value={1.5}>1.5x</option>
                          </select>
                        </div>
                        <video
                          ref={videoElementRef}
                          src={effectiveVideoUrl}
                          className="w-full aspect-video"
                          controls
                          autoPlay
                          playsInline
                          onPlay={handleStartVideo}
                          onTimeUpdate={handleVideoTimeUpdate}
                          onEnded={handleVideoEnded}
                        />
                      </div>
                    )
                  ) : (
                    <div className="aspect-video flex items-center justify-center text-white text-center px-3 sm:px-6">
                      <div>
                        <div className="text-sm sm:text-base lg:text-lg font-semibold">Video not available</div>
                        <div className="text-xs sm:text-sm text-gray-300 mt-2">
                          This activity doesn't have a video URL yet.
                        </div>
                      </div>
                    </div>
                  )}

                  {activityState === 'completed' ? (
                    <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3 bg-black/60 rounded-md px-2 sm:px-3 py-1.5 sm:py-2 text-white text-xs sm:text-sm flex items-center gap-1 sm:gap-2">
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
                      Completed
                    </div>
                  ) : null}
                </div>

                {(activityState === 'in-progress' || (effectiveVideoUrl && !isYouTubeUrl(effectiveVideoUrl))) && (
                  <div className="space-y-1.5 sm:space-y-2">
                    <div className="flex justify-between text-xs sm:text-sm text-gray-300">
                      <span>Progress</span>
                      <span>{Math.round(videoProgress)}%</span>
                    </div>
                    <Progress value={videoProgress} className="h-1.5 sm:h-2" />
                  </div>
                )}
              </>
            )}

            {/* EXPLORE */}
            {currentItem.type === 'Explore' && (
              <div className="w-full h-full min-h-[500px] flex flex-col">
                {currentItemExtras?.simulationLoading ? (
                  <div className="flex items-center justify-center py-8 sm:py-12 flex-1">
                    <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-teal-400" />
                    <span className="ml-2 sm:ml-3 text-gray-300 mt-2 sm:mt-4 text-xs sm:text-sm">Loading simulation...</span>
                  </div>
                ) : currentItemExtras?.simulationError ? (
                  <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 sm:p-4 text-center">
                    <p className="text-red-400 mb-3 sm:mb-4 text-xs sm:text-sm">{currentItemExtras.simulationError}</p>
                    {/* Fallback to old mockup */}
                    <div className="bg-gradient-to-br from-[hsl(var(--card-bg))] to-[hsl(var(--card-bg))]/80 border-2 border-[hsl(var(--card-border))] rounded-lg p-4 sm:p-8">
                      <h3 className="text-sm sm:text-base lg:text-lg font-bold text-white mb-4 sm:mb-6 text-center">
                        Click all interactions to complete!
                      </h3>
                      {/* ... existing fallback render ... */}
                    </div>
                  </div>
                ) : currentItemExtras?.exploreSimulation ? (
                  <div className="flex-1 w-full bg-slate-950/50 rounded-lg overflow-hidden border border-slate-800">
                    <SimulationRenderer
                      key={currentItem.id}
                      simulationType={currentItemExtras.exploreSimulation.simulation_type}
                      simulationData={currentItemExtras.exploreSimulation.simulation_data}
                      onComplete={() => {
                        setActivityState('completed');
                        if (!isAlreadyCompleted) {
                          onComplete(currentItem.id, currentItem.xpReward);
                        }
                      }}
                      isCompleted={isAlreadyCompleted || activityState === 'completed'}
                    />
                  </div>
                ) : (
                  /* Fallback for when no simulation data exists yet */
                  <div className="bg-gradient-to-br from-[hsl(var(--card-bg))] to-[hsl(var(--card-bg))]/80 border-2 border-[hsl(var(--card-border))] rounded-lg p-4 sm:p-8">
                    <h3 className="text-sm sm:text-base lg:text-lg font-bold text-white mb-4 sm:mb-6 text-center">
                      Explore Activity
                    </h3>
                    <p className="text-center text-gray-400 text-xs sm:text-sm">Content is being prepared...</p>
                  </div>
                )}
              </div>
            )}

            {/* PRACTICE */}
            {currentItem.type === 'Practice' && (
              <div className="space-y-3 sm:space-y-4 lg:space-y-6">
                {currentItem.questions?.map((question, qIdx) => (
                  <div key={question.id} className="bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg p-3 sm:p-4 lg:p-6">
                    <h3 className="font-bold text-white mb-3 sm:mb-4 text-xs sm:text-sm lg:text-base">
                      Q{qIdx + 1}: {question.question}
                    </h3>
                    <div className="space-y-1.5 sm:space-y-2">
                      {question.options.map((option, optIdx) => {
                        const isSelected = selectedAnswers[question.id] === optIdx;
                        return (
                          <Button
                            key={optIdx}
                            onClick={() => handleAnswerSelect(question.id, optIdx)}
                            variant="outline"
                            className={`w-full justify-start text-left h-auto py-2 sm:py-3 px-3 sm:px-4 text-xs sm:text-sm ${isSelected
                              ? 'bg-[hsl(var(--primary))] border-[hsl(var(--primary))] text-white'
                              : 'bg-[hsl(var(--card-bg))] hover:bg-[hsl(var(--sidebar-hover))] text-gray-200'
                              }`}
                          >
                            <div className="flex items-center gap-2 sm:gap-3">
                              <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-white bg-white' : 'border-gray-400'
                                }`}>
                                {isSelected && <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-[hsl(var(--primary))]" />}
                              </div>
                              <span>{option}</span>
                            </div>
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                <Button
                  onClick={handleSubmitPractice}
                  disabled={!allQuestionsAnswered || activityState === 'completed'}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-3 sm:py-4 lg:py-6 text-sm sm:text-base lg:text-lg"
                >
                  {activityState === 'completed' ? (
                    <><Check className="w-4 h-4 sm:w-5 sm:h-5 mr-2" /> Submitted</>
                  ) : (
                    'Submit Answers'
                  )}
                </Button>
              </div>
            )}

            {/* CURIOSITY */}
            {currentItem.type === 'Curiosity' && (
              <>
                {(() => {
                  const curiosityTitle = (resolvedCuriosity?.title || currentItem?.title || 'Curiosity').toString().trim();

                  const fallbackParsed = parseCuriositySegments(currentItem?.content);
                  const curiosityQuickIdea = (resolvedCuriosity?.intro || fallbackParsed.intro || '').toString();
                  const curiosityExplanation = (resolvedCuriosity?.explanation || fallbackParsed.explanation || '').toString();

                  const steps = ['Question', 'Quick idea', 'Explanation'];
                  const atFinalStep = curiosityStep >= 2;

                  return (
                    <>
                      <div className="text-base sm:text-lg lg:text-xl font-bold text-white break-words">{curiosityTitle}</div>

                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                        {steps.map((label, idx) => {
                          const isActive = idx === curiosityStep;
                          const isDone = idx < curiosityStep;
                          return (
                            <button
                              key={label}
                              type="button"
                              onClick={() => setCuriosityStep(idx)}
                              className={
                                "px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs font-semibold border transition-colors whitespace-nowrap " +
                                (isActive
                                  ? "bg-yellow-500/20 border-yellow-500/50 text-yellow-300"
                                  : isDone
                                    ? "bg-green-500/15 border-green-500/40 text-green-300"
                                    : "bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-gray-300 hover:text-white")
                              }
                            >
                              {idx + 1}. {label}
                            </button>
                          );
                        })}
                      </div>

                      <div className="bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg p-3 sm:p-4 lg:p-6">
                        {curiosityStep === 0 && (
                          <div className="space-y-2 sm:space-y-3">
                            <div className="text-xs sm:text-sm font-semibold text-gray-300">Question / Statement</div>
                            <div className="text-gray-400 text-xs sm:text-sm">Reflect on the question above, then continue.</div>
                          </div>
                        )}

                        {curiosityStep === 1 && (
                          <div className="space-y-2 sm:space-y-3">
                            <div className="text-xs sm:text-sm font-semibold text-gray-300">Quick idea</div>
                            {curiosityQuickIdea ? (
                              <div className="text-gray-200 italic leading-relaxed text-xs sm:text-sm lg:text-base bg-yellow-500/10 border border-yellow-500/25 rounded-lg p-3 sm:p-4">
                                {curiosityQuickIdea}
                              </div>
                            ) : (
                              <div className="text-gray-400 text-xs sm:text-sm">No quick idea provided for this curiosity.</div>
                            )}
                          </div>
                        )}

                        {curiosityStep >= 2 && (
                          <div className="space-y-3 sm:space-y-4">
                            <div className="flex items-center justify-between gap-2 sm:gap-3">
                              <div>
                                <div className="text-xs sm:text-sm font-semibold text-gray-300">Explanation</div>
                                <div className="text-xs text-gray-400">Reveal after learning ahead.</div>
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => setCuriosityReveal((v) => !v)}
                                className="border-[hsl(var(--card-border))] text-white hover:text-white h-8 sm:h-10 text-xs sm:text-sm px-3 sm:px-4"
                              >
                                {curiosityReveal ? 'Hide' : 'Reveal'}
                              </Button>
                            </div>

                            {curiosityReveal ? (
                              <div className="bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg p-3 sm:p-4 max-h-48 sm:max-h-72 overflow-y-auto">
                                <div className="prose prose-invert max-w-none">
                                  {curiosityExplanation
                                    ? curiosityExplanation.split('\n\n').map((paragraph, idx) => (
                                      <p key={idx} className="text-gray-200 leading-relaxed mb-3 sm:mb-4 text-xs sm:text-sm">
                                        {paragraph}
                                      </p>
                                    ))
                                    : (
                                      <p className="text-gray-400 text-xs sm:text-sm">No explanation available.</p>
                                    )}
                                </div>
                              </div>
                            ) : (
                              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 sm:p-4 text-yellow-200 text-xs sm:text-sm">
                                Explanation is hidden. Learn a bit more, then reveal it.
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 sm:gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setCuriosityStep((s) => Math.max(0, s - 1))}
                          disabled={curiosityStep === 0}
                          className="flex-1 border-[hsl(var(--card-border))] text-white hover:text-white disabled:opacity-50 h-9 sm:h-10 text-xs sm:text-sm"
                        >
                          Back
                        </Button>

                        {!atFinalStep ? (
                          <Button
                            type="button"
                            onClick={() => setCuriosityStep((s) => Math.min(2, s + 1))}
                            className="flex-1 bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700 text-white font-bold h-9 sm:h-10 text-xs sm:text-sm"
                          >
                            Next
                          </Button>
                        ) : (
                          <Button
                            onClick={handleMarkAsRead}
                            disabled={activityState === 'completed'}
                            className="flex-1 bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed h-9 sm:h-10 text-xs sm:text-sm"
                          >
                            {activityState === 'completed' ? (
                              <>
                                <Check className="w-4 h-4 sm:w-5 sm:h-5 mr-2" /> Marked as Read
                              </>
                            ) : (
                              'Mark as Read'
                            )}
                          </Button>
                        )}
                      </div>
                    </>
                  );
                })()}
              </>
            )}
          </div>

          {/* Completion Badge */}
          {canProceed && (
            <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/50 rounded-lg p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                <div className="text-green-400 font-bold text-sm sm:text-base lg:text-lg">
                  ✓ Activity Completed! {!isAlreadyCompleted && `+${currentItem.xpReward} Gems`}
                </div>
                <Button
                  onClick={handleNext}
                  className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--teal-glow))] text-white font-bold h-9 sm:h-10 text-xs sm:text-sm w-full sm:w-auto"
                >
                  {currentIndex < items.length - 1 ? (
                    <>Next Activity <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-2" /></>
                  ) : (
                    'Complete Journey'
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Floating Chat Button - Visible only when chat panel is closed */}
      {!chatMode && onToggleChatMode && (
        <BudleeAIIcon
          size="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16"
          iconSize="w-6 h-6 sm:w-7 h-7 lg:w-8 h-8"
          position="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 lg:bottom-4 lg:right-6"
          className="z-[100]"
          onClick={onToggleChatMode}
          showBadge={true}
        />
      )}
    </div>
  );
}
